import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronRight, Loader2 } from "lucide-react";
import { InstagramIcon } from "@/components/InstagramIcon";
import { toast } from "sonner";
import { SettingsMetaContent } from "./SettingsMetaPageLayout";

interface MetaAccount {
  _id: string;
  instagramUsername?: string;
  type?: string;
  name?: string;
}

interface MetaGraphBusiness {
  id: string;
  name: string;
}

export function SettingsMeta() {
  const [metaAccount, setMetaAccount] = useState<MetaAccount | null>(null);
  const [businesses, setBusinesses] = useState<MetaGraphBusiness[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisconnectingMeta, setIsDisconnectingMeta] = useState(false);

  const isConnected = metaAccount?.type === "facebook";

  useEffect(() => {
    async function fetchMetaAccount() {
      setIsLoading(true);
      try {
        const metaResponse = await fetch("/api/meta-account?type=facebook");
        if (metaResponse.ok) {
          const metaData = await metaResponse.json();
          if (metaData.metaAccount) {
            setMetaAccount(metaData.metaAccount);
          }
          if (metaData.businesses) {
            setBusinesses(metaData.businesses);
          }
        } else if (metaResponse.status !== 404) {
          console.error("Failed to fetch Meta account");
        }
      } catch (error) {
        console.error("Failed to fetch Meta account:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchMetaAccount();
  }, []);

  const handleDisconnectMeta = async () => {
    if (!confirm("Are you sure you want to disconnect your Meta account?")) {
      return;
    }

    setIsDisconnectingMeta(true);
    try {
      const response = await fetch("/api/meta/facebook/disconnect", {
        method: "POST",
      });

      if (response.ok) {
        setMetaAccount(null);
        setBusinesses([]);
        toast.success("Meta account disconnected successfully");
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to disconnect Meta account");
      }
    } catch (error) {
      console.error("Error disconnecting Meta:", error);
      toast.error(error instanceof Error ? error.message : "Failed to disconnect Meta account");
    } finally {
      setIsDisconnectingMeta(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <SettingsMetaContent>
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">Meta</h3>
            {!isConnected && (
              <p className="text-sm text-muted-foreground">
                Connect your Facebook account for Instagram messaging and comments
              </p>
            )}
            {isConnected && (
              <div className="flex items-center gap-2 mt-2">
                <p className="text-sm text-green-600">Connected</p>
                {metaAccount?.name && (
                  <p className="text-sm text-muted-foreground">{metaAccount.name}</p>
                )}
                {metaAccount?.instagramUsername && (
                  <a
                    href={`https://instagram.com/${metaAccount.instagramUsername}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
                  >
                    <InstagramIcon className="text-pink-600" />
                    @{metaAccount.instagramUsername}
                  </a>
                )}
              </div>
            )}
          </div>
          {!isConnected ? (
            <Button asChild>
              <a href="/api/meta/facebook/connect">Connect</a>
            </Button>
          ) : (
            <Button
              onClick={handleDisconnectMeta}
              disabled={isDisconnectingMeta}
              variant="destructive"
            >
              {isDisconnectingMeta ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                "Disconnect"
              )}
            </Button>
          )}
        </div>
      </div>

      {isConnected && businesses.length > 0 && (
        <div className="space-y-2">
          {businesses.map((business) => (
            <Link
              key={business.id}
              to={`/settings/meta/businesses/${business.id}`}
              className="flex items-center justify-between border rounded-lg p-4 hover:bg-muted/50 transition-colors"
            >
              <span className="font-medium">{business.name}</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </SettingsMetaContent>
  );
}
